import React, { useState, useEffect } from 'react';
import './FounderDashboard.css';
import { usersAPI, giveawaysAPI, announcementsAPI } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import ProgressBar from './ProgressBar';
import { getAllPermissions, saveAllPermissions, PERMISSIONS, PERMISSION_DESCRIPTIONS } from '../utils/permissions';
import UserManagement from './UserManagement';
import ActivityLog from './ActivityLog';
import GiveawayManagement from './GiveawayManagement';

function FounderDashboard({ currentUser }) {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalGiveaways: 0,
    activeGiveaways: 0,
    totalEntries: 0,
    totalWins: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [users, giveaways] = await Promise.all([
        usersAPI.getAll(),
        giveawaysAPI.getAll(),
      ]);

      const activeUsers = users.filter(u => !u.locked && new Date(u.lastLogin || u.createdAt) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
      let totalEntries = 0;
      users.forEach(user => {
        Object.values(user.entries || {}).forEach(entry => {
          if (new Date(entry.expiresAt) > new Date()) {
            totalEntries += entry.count || 0;
          }
        });
      });

      setStats({
        totalUsers: users.length,
        activeUsers: activeUsers.length,
        totalGiveaways: giveaways.length,
        activeGiveaways: giveaways.filter(g => g.status === 'active').length,
        totalEntries,
        totalWins: giveaways.filter(g => g.winner).length,
      });

      // Recent activity (mock for now)
      setRecentActivity([
        { type: 'user', action: 'New user registered', user: 'NewUser123', time: '5 minutes ago' },
        { type: 'giveaway', action: 'New giveaway created', title: 'Discord Nitro Giveaway', time: '1 hour ago' },
        { type: 'entry', action: 'User entered giveaway', user: 'LuckyUser', time: '2 hours ago' },
      ]);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser || currentUser.role !== 'founder') {
    return (
      <div className="founder-dashboard-page">
        <div className="founder-dashboard-container">
          <h1>Access Denied</h1>
          <p>Only the founder can access this dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  return (
    <div className="founder-dashboard-page">
      <div className="founder-dashboard-container">
        <div className="founder-dashboard-header">
          <h1>👑 Founder Dashboard</h1>
          <p>Complete control and analytics for NitroDrop</p>
        </div>

        <div className="founder-dashboard-tabs">
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={activeTab === 'analytics' ? 'active' : ''}
            onClick={() => setActiveTab('analytics')}
          >
            Analytics
          </button>
          <button
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button
            className={activeTab === 'giveaways' ? 'active' : ''}
            onClick={() => setActiveTab('giveaways')}
          >
            Giveaways
          </button>
          <button
            className={activeTab === 'permissions' ? 'active' : ''}
            onClick={() => setActiveTab('permissions')}
          >
            Permissions
          </button>
          <button
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        <div className="founder-dashboard-content">
          {activeTab === 'overview' && (
            <div className="dashboard-overview">
              <div className="stats-grid-large">
                <div className="stat-card-large">
                  <div className="stat-icon-large">👥</div>
                  <div className="stat-content-large">
                    <h3>{stats.totalUsers}</h3>
                    <p>Total Users</p>
                    <span className="stat-change">+{stats.activeUsers} active</span>
                  </div>
                </div>
                <div className="stat-card-large">
                  <div className="stat-icon-large">🎁</div>
                  <div className="stat-content-large">
                    <h3>{stats.totalGiveaways}</h3>
                    <p>Total Giveaways</p>
                    <span className="stat-change">{stats.activeGiveaways} active</span>
                  </div>
                </div>
                <div className="stat-card-large">
                  <div className="stat-icon-large">🎯</div>
                  <div className="stat-content-large">
                    <h3>{stats.totalEntries}</h3>
                    <p>Total Entries</p>
                    <span className="stat-change">All time</span>
                  </div>
                </div>
                <div className="stat-card-large">
                  <div className="stat-icon-large">🏆</div>
                  <div className="stat-content-large">
                    <h3>{stats.totalWins}</h3>
                    <p>Total Wins</p>
                    <span className="stat-change">Completed</span>
                  </div>
                </div>
              </div>

              <div className="dashboard-widgets">
                <div className="widget">
                  <h3>Recent Activity</h3>
                  <div className="activity-list">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="activity-item">
                        <div className="activity-icon">
                          {activity.type === 'user' && '👤'}
                          {activity.type === 'giveaway' && '🎁'}
                          {activity.type === 'entry' && '🎯'}
                        </div>
                        <div className="activity-content">
                          <p><strong>{activity.action}</strong></p>
                          <p className="activity-details">
                            {activity.user || activity.title}
                          </p>
                        </div>
                        <div className="activity-time">{activity.time}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="widget">
                  <h3>Quick Actions</h3>
                  <div className="quick-actions-grid">
                    <button className="action-btn">Create Giveaway</button>
                    <button className="action-btn">Manage Users</button>
                    <button className="action-btn">View Analytics</button>
                    <button className="action-btn">System Settings</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="dashboard-analytics">
              <h2>Platform Analytics</h2>
              <div className="analytics-grid">
                <div className="analytics-card">
                  <h3>User Growth</h3>
                  <div className="chart-placeholder">
                    <ProgressBar value={75} max={100} label="Growth Rate" />
                  </div>
                </div>
                <div className="analytics-card">
                  <h3>Giveaway Activity</h3>
                  <div className="chart-placeholder">
                    <ProgressBar value={60} max={100} label="Active Rate" />
                  </div>
                </div>
                <div className="analytics-card">
                  <h3>Entry Distribution</h3>
                  <div className="chart-placeholder">
                    <ProgressBar value={85} max={100} label="Participation" />
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '2rem' }}>
                <ActivityLog />
              </div>
            </div>
          )}

          {activeTab === 'giveaways' && (
            <div className="dashboard-giveaways">
              <GiveawayManagement currentUser={currentUser} />
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="dashboard-permissions">
              <PermissionManager />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="dashboard-settings">
              <h2>Platform Settings</h2>
              <div className="settings-grid">
                <div className="setting-card">
                  <h3>General Settings</h3>
                  <div className="setting-item">
                    <label>Platform Name</label>
                    <input type="text" defaultValue="NitroDrop" className="setting-input" />
                  </div>
                  <div className="setting-item">
                    <label>Maintenance Mode</label>
                    <label className="toggle-switch">
                      <input type="checkbox" />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className="setting-card">
                  <h3>Security Settings</h3>
                  <div className="setting-item">
                    <label>Require Email Verification</label>
                    <label className="toggle-switch">
                      <input type="checkbox" defaultChecked />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <div className="setting-item">
                    <label>Rate Limiting</label>
                    <label className="toggle-switch">
                      <input type="checkbox" defaultChecked />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className="setting-card">
                  <h3>Notification Settings</h3>
                  <div className="setting-item">
                    <label>Email Notifications</label>
                    <label className="toggle-switch">
                      <input type="checkbox" defaultChecked />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <div className="setting-item">
                    <label>Discord Webhooks</label>
                    <label className="toggle-switch">
                      <input type="checkbox" defaultChecked />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
              <button className="save-settings-btn">Save Settings</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PermissionManager() {
  const rankNames = {
    'founder': 'Founder',
    'co-founder': 'Co-Founder',
    'manager': 'Manager',
    'administrator': 'Administrator',
    'head-admin': 'Head Admin',
    'admin': 'Admin',
    'mod': 'Moderator',
    'user': 'User',
  };

  const [ranks, setRanks] = useState(() => {
    const allPerms = getAllPermissions();
    return Object.keys(rankNames).map(id => ({
      id,
      name: rankNames[id],
      permissions: allPerms[id] || [],
    }));
  });

  const allPermissions = Object.keys(PERMISSIONS).map(key => ({
    id: PERMISSIONS[key],
    name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: PERMISSION_DESCRIPTIONS[PERMISSIONS[key]],
  }));

  const togglePermission = (rankId, permissionId) => {
    setRanks(prev => prev.map(rank => {
      if (rank.id !== rankId) return rank;
      
      if (permissionId === 'all') {
        return {
          ...rank,
          permissions: rank.permissions.includes('all') ? [] : ['all'],
        };
      }

      const hasAll = rank.permissions.includes('all');
      if (hasAll) {
        // If has 'all', remove it and add specific permission
        return {
          ...rank,
          permissions: [permissionId],
        };
      }

      const hasPermission = rank.permissions.includes(permissionId);
      return {
        ...rank,
        permissions: hasPermission
          ? rank.permissions.filter(p => p !== permissionId)
          : [...rank.permissions, permissionId],
      };
    }));
  };

  const hasPermission = (rankPermissions, permissionId) => {
    return rankPermissions.includes('all') || rankPermissions.includes(permissionId);
  };

  return (
    <div className="permission-manager">
      <h2>Permission Management</h2>
      <p className="permission-description">
        Configure permissions for each rank. Users with a rank will have access to the permissions assigned to that rank.
      </p>

      <div className="permissions-table">
        <div className="permissions-header">
          <div className="rank-column">Rank</div>
          {allPermissions.map(perm => (
            <div key={perm.id} className="permission-column" title={perm.description}>
              {perm.name}
            </div>
          ))}
        </div>

        {ranks.map(rank => (
          <div key={rank.id} className="permissions-row">
            <div className="rank-column">
              <strong>{rank.name}</strong>
            </div>
            {allPermissions.map(perm => (
              <div key={perm.id} className="permission-cell">
                <label className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={hasPermission(rank.permissions, perm.id)}
                    onChange={() => togglePermission(rank.id, perm.id)}
                    disabled={rank.id === 'founder'}
                  />
                  <span className="checkmark"></span>
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="permission-actions">
        <button 
          className="save-permissions-btn"
          onClick={() => {
            const permissionsObj = {};
            ranks.forEach(rank => {
              permissionsObj[rank.id] = rank.permissions;
            });
            if (saveAllPermissions(permissionsObj)) {
              alert('Permissions saved successfully!');
            } else {
              alert('Failed to save permissions.');
            }
          }}
        >
          Save Permissions
        </button>
        <button 
          className="reset-permissions-btn"
          onClick={() => {
            if (window.confirm('Reset all permissions to defaults? This cannot be undone.')) {
              const defaultPerms = {
                'founder': ['all'],
                'co-founder': ['manage_users', 'manage_giveaways', 'ban', 'lock', 'delete', 'moderate_chat', 'manage_announcements', 'view_analytics', 'manage_entries', 'manage_boosts'],
                'manager': ['manage_giveaways', 'lock', 'moderate_chat', 'manage_announcements', 'view_analytics'],
                'administrator': ['manage_users', 'ban', 'lock', 'moderate_chat', 'manage_announcements'],
                'head-admin': ['manage_users', 'ban', 'moderate_chat'],
                'admin': ['lock', 'moderate_chat'],
                'mod': ['moderate_chat'],
                'user': [],
              };
              saveAllPermissions(defaultPerms);
              setRanks(Object.keys(rankNames).map(id => ({
                id,
                name: rankNames[id],
                permissions: defaultPerms[id] || [],
              })));
              alert('Permissions reset to defaults!');
            }
          }}
        >
          Reset to Default
        </button>
      </div>
    </div>
  );
}

export default FounderDashboard;

